import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Dialog,
    Stack,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import AdminAuthGate from './AdminAuthGate.jsx';
import PromoForm from './PromoForm.jsx';
import { subscribeToPromos, deletePromo } from '../../promos/promoFirestore';
import { PROMO_KIND_CODE } from '../../promos/promoTypes';
import { PALETTE } from '../../theme';

/**
 * Format a millis timestamp for the admin table. Empty value → em-dash.
 */
function fmt(ms) {
    if (!ms) return '—';
    const d = new Date(ms);
    return d.toLocaleString();
}

const AdminPageInner = () => {
    const [promos, setPromos] = useState([]);
    const [editing, setEditing] = useState(null); // null = closed, {} = create, {...promo} = edit
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Live subscription so changes from another tab (or the form's own
        // upsert) reflect instantly.
        const unsub = subscribeToPromos((list) => {
            setPromos(list);
            setLoading(false);
        });
        return unsub;
    }, []);

    const handleDelete = async (promo) => {
        const label = promo.name || promo.code || promo.id;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete promo "${label}"? This cannot be undone.`)) return;
        try {
            await deletePromo(promo.id);
        } catch (err) {
            // eslint-disable-next-line no-alert
            window.alert(`Delete failed: ${err.message}`);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>Promos</Typography>
                    <Typography variant="body2" sx={{ color: PALETTE.textMuted }}>
                        Influencer codes + sitewide date promos. Discounts are routed to
                        manually-cloned Zazzle SKUs at lower royalty — set the cloned IDs
                        in <code>src/promos/promoSkus.js</code>.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setEditing({})}
                >
                    New promo
                </Button>
            </Box>

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Kind</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell>Starts</TableCell>
                            <TableCell>Ends</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={8}>Loading…</TableCell></TableRow>
                        )}
                        {!loading && promos.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ color: PALETTE.textMuted }}>
                                    No promos yet. Create your first one with the button above.
                                </TableCell>
                            </TableRow>
                        )}
                        {promos.map((p) => (
                            <TableRow key={p.id}>
                                <TableCell>{p.name || <em style={{ color: '#999' }}>(no name)</em>}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={p.kind === PROMO_KIND_CODE ? 'Code' : 'Date'}
                                        color={p.kind === PROMO_KIND_CODE ? 'primary' : 'default'}
                                    />
                                </TableCell>
                                <TableCell><code>{p.code || '—'}</code></TableCell>
                                <TableCell>{p.discountPercent}%</TableCell>
                                <TableCell>{fmt(p.startsAt)}</TableCell>
                                <TableCell>{fmt(p.endsAt)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={p.active ? 'Active' : 'Off'}
                                        color={p.active ? 'success' : 'default'}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <IconButton size="small" onClick={() => setEditing(p)} aria-label="Edit">
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(p)} aria-label="Delete">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
                {editing !== null && (
                    <PromoForm
                        initialPromo={editing.id ? editing : null}
                        onSaved={() => setEditing(null)}
                        onCancel={() => setEditing(null)}
                    />
                )}
            </Dialog>
        </Container>
    );
};

const AdminPage = () => (
    <AdminAuthGate>
        <AdminPageInner />
    </AdminAuthGate>
);

export default AdminPage;
